import { z } from '../../utils/zod-extend'

/**
 * - default: デフォルトの遷移
 * - question: 質問に遷移
 * - launcher_content_thanks: ランチャーのコンテンツページに遷移
 */
export const campaignLogicActionDetailToTypes = ['default', 'question', 'launcher_content_thanks'] as const
export type CampaignLogicActionDetailToType = typeof campaignLogicActionDetailToTypes[number]

const textTypeOperator = z.enum(['equal', 'not_equal', 'begins_with', 'ends_with', 'contains', 'not_contains'])
const fileTypeOperator = z.enum(['answered'])
const choiceTypeOperator = z.enum(['is', 'is_not'])
const numericTypeOperator = z.enum(['equal', 'not_equal', 'lower_than', 'lower_equal_than', 'greater_than', 'greater_equal_than'])
const dateTypeOperator = z.enum(['on', 'not_on', 'earlier_than', 'earlier_than_or_on', 'later_than', 'later_than_or_on'])
const panelTypeOperator = z.enum(['answered'])

/**
 * logic conditionのvalueの型 - date
 */
const logicConditionValueQuestionDateSchema = z.object({
  questionType: z.literal('date'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.coerce.date(),
  valueOp: dateTypeOperator,
})
export type LogicConditionValueQuestionDateTypeValue = z.infer<typeof logicConditionValueQuestionDateSchema>

/**
 * logic conditionのvalueの型 - number
 */
const logicConditionValueQuestionNumberSchema = z.object({
  questionType: z.literal('number'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.coerce.number(),
  valueOp: numericTypeOperator,
})
export type LogicConditionValueQuestionNumberTypeValue = z.infer<typeof logicConditionValueQuestionNumberSchema>

/**
 * logic conditionのvalueの型 - boolean
 */
const logicConditionValueQuestionBooleanSchema = z.object({
  questionType: z.literal('boolean'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  valueOp: choiceTypeOperator,
  value: z.coerce.boolean(),
})
export type LogicConditionValueQuestionBooleanTypeValue = z.infer<typeof logicConditionValueQuestionBooleanSchema>

/**
 * logic conditionのvalueの型 - radiogroup
 */
const logicConditionValueQuestionRadiogroupSchema = z.object({
  questionType: z.literal('radiogroup'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.string().min(1, { message: '必須です' }),
  valueOp: choiceTypeOperator,
})
export type LogicConditionValueQuestionRadiogroupTypeValue = z.infer<typeof logicConditionValueQuestionRadiogroupSchema>

/**
 * logic conditionのvalueの型 - text
 */
const logicConditionValueQuestionTextSchema = z.object({
  questionType: z.literal('text'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.string().min(1, { message: '必須です' }),
  valueOp: textTypeOperator,
})
export type LogicConditionValueQuestionTextTypeValue = z.infer<typeof logicConditionValueQuestionTextSchema>

/**
 * logic conditionのvalueの型 - file
 */
const logicConditionValueQuestionFileSchema = z.object({
  questionType: z.literal('file'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.boolean(),
  valueOp: fileTypeOperator,
})
export type LogicConditionValueQuestionFileTypeValue = z.infer<typeof logicConditionValueQuestionFileSchema>

/**
 * logic conditionのvalueの型 - comment
 */
const logicConditionValueQuestionCommentSchema = z.object({
  questionType: z.literal('comment'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.string().min(1, { message: '必須です' }),
  valueOp: textTypeOperator,
})
export type LogicConditionValueQuestionCommentTypeValue = z.infer<typeof logicConditionValueQuestionCommentSchema>

/**
 * logic conditionのvalueの型 - checkbox
 * checkboxは複数選択だが、条件分岐での選択は1つだけに限定する -> valueはarrayではなくstring
 */
const logicConditionValueQuestionCheckboxSchema = z.object({
  questionType: z.literal('checkbox'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.string().min(1, { message: '必須です' }),
  valueOp: choiceTypeOperator,
})
export type LogicConditionValueQuestionCheckboxTypeValue = z.infer<typeof logicConditionValueQuestionCheckboxSchema>

/**
 * logic conditionのvalueの型 - rating
 */
const logicConditionValueQuestionRatingSchema = z.object({
  questionType: z.literal('rating'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.coerce.number(),
  valueOp: numericTypeOperator,
})
export type LogicConditionValueQuestionRatingTypeValue = z.infer<typeof logicConditionValueQuestionRatingSchema>

/**
 * logic conditionのvalueの型 - imagepicker
 */
const logicConditionValueQuestionImagepickerSchema = z.object({
  questionType: z.literal('imagepicker'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.string().min(1, { message: '必須です' }),
  valueOp: choiceTypeOperator,
})
export type LogicConditionValueQuestionImagepickerTypeValue = z.infer<typeof logicConditionValueQuestionImagepickerSchema>

/**
 * logic conditionのvalueの型 - panel
 */
const logicConditionValueQuestionPanelSchema = z.object({
  questionType: z.literal('panel'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.boolean(),
  valueOp: panelTypeOperator,
})
export type LogicConditionValueQuestionPanelTypeValue = z.infer<typeof logicConditionValueQuestionPanelSchema>

/**
 * logic conditionのvalueの型 - ranking
 */
const logicConditionValueQuestionRankingSchema = z.object({
  questionType: z.literal('ranking'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.string().min(1, { message: '必須です' }),
  valueOp: choiceTypeOperator,
})
export type LogicConditionValueQuestionRankingTypeValue = z.infer<typeof logicConditionValueQuestionRankingSchema>

/**
 * logic conditionのvalueの型 - dropdown
 */
const logicConditionValueQuestionDropdownSchema = z.object({
  questionType: z.literal('dropdown'),
  type: z.literal('question'),
  typeId: z.string().min(1, { message: '必須です' }),
  value: z.string().min(1, { message: '必須です' }),
  valueOp: choiceTypeOperator,
})
export type LogicConditionValueQuestionDropdownTypeValue = z.infer<typeof logicConditionValueQuestionDropdownSchema>

/**
 * logic conditionのvalueの型
 * checkbox / 複数選択になるので、現在サポートしない
 * text / 自由入力すぎるので、現在サポートしない
 */
const logicConditionValueQuestionSchema = z.discriminatedUnion('questionType', [
  logicConditionValueQuestionTextSchema,
  logicConditionValueQuestionCommentSchema,
  logicConditionValueQuestionRadiogroupSchema,
  logicConditionValueQuestionCheckboxSchema,
  logicConditionValueQuestionBooleanSchema,
  logicConditionValueQuestionRatingSchema,
  logicConditionValueQuestionImagepickerSchema,
  logicConditionValueQuestionFileSchema,
  logicConditionValueQuestionDateSchema,
  logicConditionValueQuestionNumberSchema,
  logicConditionValueQuestionPanelSchema,
  logicConditionValueQuestionRankingSchema,
  logicConditionValueQuestionDropdownSchema,
])
export type LogicConditionValue = z.infer<typeof logicConditionValueQuestionSchema>

/**
 * isの場合には varsは1つだけ判定する
 * and, orの場合には varsは複数判定する
 * alwaysの場合には varsは無視する
 *
 * -> 2024/09/10現在、varsは1つだけ入力できない仕様になっていて、and, orは利用されていない。
 * -> 2024/09/10現在、alwaysの場合は varsは必ずemptyになっている。
 */
const logicActionConditionSchema = z.object({
  op: z.enum(['is', 'and', 'or', 'always']),
  vars: z.array(logicConditionValueQuestionSchema),
}).superRefine((value, ctx) => {
  if (value.op !== 'always' && value.vars.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '必須です',
      path: ['vars'],
    })
  }
})
export type LogicActionCondition = z.infer<typeof logicActionConditionSchema>

/**
 * default次へ
 */
const campaignLogicDetailToDefaultSchema = z.object({
  type: z.literal('default'),
  value: z.string().optional(),
})
/**
 * ページに遷移
 */
const campaignLogicDetailToQuestionSchema = z.object({
  type: z.literal('question'),
  value: z.string().min(1, { message: '必須です' }), // jumpするターゲットのuniqueなid - survey question element#name
})
/**
 * ランチャーのコンテンツページに遷移
 */
const campaignLogicDetailToLauncherContentThanksSchema = z.object({
  type: z.literal('launcher_content_thanks'),
  value: z.string().min(1, { message: '必須です' }), // jumpするターゲットのuniqueなid - launcher_content_id
})
const campaignLogicDetailSchema = z.object({
  to: z.discriminatedUnion('type', [
    campaignLogicDetailToDefaultSchema,
    campaignLogicDetailToQuestionSchema,
    campaignLogicDetailToLauncherContentThanksSchema,
  ]),
})
export type CampaignLogicDetail = z.infer<typeof campaignLogicDetailSchema>

const campaignLogicActionSchema = z.object({
  action: z.literal('jump'), // 今はjumpのみ
  detail: campaignLogicDetailSchema,
  condition: logicActionConditionSchema,
})
export type CampaignLogicAction = z.infer<typeof campaignLogicActionSchema>

/**
 * ベースはtypeformで、actions.condition.varsからは型をカスタムしている。
 * https://www.typeform.com/developers/create/logic-jumps/
 */
export const campaignLogicSingleSchema = z.object({
  type: z.enum(['question']), // 今はquestionのみ(question以外を作ることはほぼない気がする）
  ref: z.string().min(1, { message: '必須です' }), // survey questionのname(unique id)
  actions: z.array(campaignLogicActionSchema),
})
export type CampaignLogicSingle = z.infer<typeof campaignLogicSingleSchema>
